import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, MoreHorizontal, ChevronRight } from 'lucide-react';
import i18n from '../../i18n/index.js';

/**
 * ContextualActionsMenu - Dropdown menu for row actions
 * Features:
 * - Customizable trigger icon
 * - Grouped actions with dividers
 * - Keyboard navigation
 * - Nested submenus
 * - Danger action styling
 */
function ContextualActionsMenu({
  actions = [],
  triggerIcon: TriggerIcon = MoreVertical,
  horizontal = false,
  buttonClassName = '',
  menuClassName = '',
  align = 'right',
  disabled = false,
  onOpen,
  onClose
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [dropDirection, setDropDirection] = useState('down');
  // Viewport-fixed coordinates computed from the trigger's bounding rect.
  // The menu is rendered via a portal on document.body (position: fixed) so it
  // escapes any ancestor with overflow:auto/hidden that would otherwise clip it.
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, right: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveSubmenu(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      onOpen && onOpen();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
        setActiveSubmenu(null);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    const newState = !isOpen;
    if (newState && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedHeight = Math.min(400, actions.length * 36 + 16);
      const direction = spaceBelow < estimatedHeight && spaceAbove > spaceBelow ? 'up' : 'down';
      setDropDirection(direction);
      // Compute viewport-fixed position for the portal-mounted menu.
      setMenuPos({
        top: direction === 'down' ? rect.bottom + 4 : rect.top - 4,
        left: align === 'right' ? null : rect.left,
        right: align === 'right' ? window.innerWidth - rect.right : null,
      });
    }
    setIsOpen(newState);
    if (!newState) {
      setActiveSubmenu(null);
      onClose && onClose();
    }
  };

  // Close the menu on scroll — stale coordinates would drift otherwise.
  // Using capture so we catch scroll on any ancestor (the table container).
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => {
      setIsOpen(false);
      setActiveSubmenu(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  const handleActionClick = (action, e) => {
    e.stopPropagation();

    if (action.disabled) return;

    if (action.submenu) {
      setActiveSubmenu(activeSubmenu === action.key ? null : action.key);
      return;
    }

    if (action.onClick) {
      action.onClick(e);
    }

    setIsOpen(false);
    setActiveSubmenu(null);
    onClose && onClose();
  };

  const Icon = horizontal ? MoreHorizontal : TriggerIcon;

  return (
    <div className="relative inline-block">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        disabled={disabled}
        className={`p-2 rounded-lg transition-colors ${
          disabled
            ? 'text-gray-300 cursor-not-allowed'
            : isOpen
              ? 'bg-gray-100 text-gray-700'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        } ${buttonClassName}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Icon className="w-5 h-5" />
      </button>

      {/* Dropdown Menu — rendered via portal to escape ancestor overflow clipping
          (e.g. table wrappers with overflow-x-auto). Positioned fixed using the
          trigger's bounding rect. Closes on scroll/resize to avoid stale coords. */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: dropDirection === 'up' ? 'auto' : menuPos.top,
            bottom: dropDirection === 'up' ? (window.innerHeight - menuPos.top) : 'auto',
            left: menuPos.left ?? 'auto',
            right: menuPos.right ?? 'auto',
          }}
          className={`z-50 py-1 bg-white rounded-lg shadow-xl border border-gray-200 min-w-48 max-h-[80vh] overflow-y-auto ${menuClassName}`}
        >
          {actions.map((action, index) => {
            // Divider
            if (action.divider) {
              return <div key={index} className="my-1 border-t border-gray-200" />;
            }

            // Group header
            if (action.group) {
              return (
                <div key={index} className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {action.group}
                </div>
              );
            }

            const hasSubmenu = action.submenu && action.submenu.length > 0;
            const isSubmenuOpen = activeSubmenu === action.key;

            return (
              <div key={action.key || index} className="relative">
                <button
                  onClick={(e) => handleActionClick(action, e)}
                  disabled={action.disabled}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors ${
                    action.disabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : action.danger
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {action.icon && (
                    <action.icon className={`w-4 h-4 ${
                      action.danger ? 'text-red-500' : 'text-gray-400'
                    }`} />
                  )}
                  <span className="flex-1">{action.label}</span>
                  {action.shortcut && (
                    <span className="text-xs text-gray-400 ml-2">{action.shortcut}</span>
                  )}
                  {hasSubmenu && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {/* Submenu */}
                {hasSubmenu && isSubmenuOpen && (
                  <div className={`absolute top-0 py-1 bg-white rounded-lg shadow-xl border border-gray-200 min-w-40 ${
                    align === 'right' ? 'right-full mr-1' : 'left-full ml-1'
                  }`}>
                    {action.submenu.map((subAction, subIndex) => (
                      <button
                        key={subAction.key || subIndex}
                        onClick={(e) => handleActionClick(subAction, e)}
                        disabled={subAction.disabled}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors ${
                          subAction.disabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : subAction.danger
                              ? 'text-red-600 hover:bg-red-50'
                              : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {subAction.icon && (
                          <subAction.icon className={`w-4 h-4 ${
                            subAction.danger ? 'text-red-500' : 'text-gray-400'
                          }`} />
                        )}
                        <span className="flex-1">{subAction.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Common action presets for fleet management
 */
export const COMMON_ACTIONS = {
  edit: (onClick) => ({
    key: 'edit',
    label: i18n.t('common.actions.edit'),
    icon: require('lucide-react').Edit,
    shortcut: 'E',
    onClick
  }),
  delete: (onClick) => ({
    key: 'delete',
    label: i18n.t('common.actions.delete'),
    icon: require('lucide-react').Trash2,
    danger: true,
    onClick
  }),
  duplicate: (onClick) => ({
    key: 'duplicate',
    label: i18n.t('common.entity.actions.duplicate'),
    icon: require('lucide-react').Copy,
    onClick
  }),
  archive: (onClick) => ({
    key: 'archive',
    label: i18n.t('common.entity.actions.archive'),
    icon: require('lucide-react').Archive,
    onClick
  }),
  export: (onClick) => ({
    key: 'export',
    label: i18n.t('common.actions.export'),
    icon: require('lucide-react').Download,
    onClick
  }),
  print: (onClick) => ({
    key: 'print',
    label: i18n.t('common.actions.print'),
    icon: require('lucide-react').Printer,
    onClick
  }),
  divider: () => ({ divider: true })
};

export default ContextualActionsMenu;
